import { Tag, FormInstance } from "antd";
import { QueryOperation, SearchQuery } from "../code/search-query/search-query";
import {
  buildSearchQuery,
  comboboxValue,
  dateRangeValue,
  QueryFieldMapping,
} from "../code/search-query/query-mapper";
import { DataColumn } from "../controls/Data.default";
import { DynamicSchema } from "../controls/DynamicForm.default";
import { InputObject } from "../controls/default";
import { Dayjs } from "dayjs";
import {
  createSearchRequest,
  createScopedFilterRequest,
} from "../hooks/useSearchRequest";
import {
  getCompanies,
  getCompanyDepartments,
  getPlatformUsers,
} from "../repository/data.repository";
import { NotificationDeliveryStatus } from "../repository/data.default";
import { createStatusColorMap } from "../utils/colors";

export interface ReceiptsViewForm {
  readonly company: InputObject;
  readonly department: InputObject;
  readonly user: InputObject;
  readonly notificationTitle: string;
  readonly notificationMessage: string;
  readonly deliveryStatus: InputObject;
  readonly isRead: boolean;
  readonly readAt: Dayjs[];
  readonly createdOn: Dayjs[];
}

// Channel dispatch outcome colour — shared so the grid tag stays consistent.
export const deliveryStatusColor = createStatusColorMap<string>({
  [NotificationDeliveryStatus.Pending]: "gold",
  [NotificationDeliveryStatus.Sent]: "blue",
  [NotificationDeliveryStatus.Failed]: "red",
});

export const deliveryStatusOptions = Object.values(
  NotificationDeliveryStatus,
).map((status) => ({ display: status, value: status }));

export const receiptsViewColumns: DataColumn[] = [
  {
    title: "User Name",
    key: "userUserName",
    width: 168,
    sort: true,
    defaultSort: "asc",
    fixed: "left",
  },
  { title: "Company", key: "companyName", width: 168, sort: true },
  { title: "Department", key: "departmentName", width: 168, sort: true },
  { title: "Title", key: "notificationTitle", width: 192, sort: true },
  { title: "Message", key: "notificationMessage", width: 192, sort: true },
  {
    title: "Delivery",
    key: "deliveryStatus",
    width: 120,
    sort: true,
    transform: (value: string) => (
      <Tag color={deliveryStatusColor(value)}>{value}</Tag>
    ),
  },
  {
    title: "Sent At",
    key: "sentAt",
    width: 168,
    sort: true,
    format: "DD/MM/YYYY HH:mm",
  },
  {
    title: "Delivered At",
    key: "deliveredAt",
    width: 168,
    sort: true,
    format: "DD/MM/YYYY HH:mm",
  },
  { title: "Is Read", key: "isRead", width: 92, sort: true, type: "badge" },
  {
    title: "Read At",
    key: "readAt",
    width: 168,
    sort: true,
    format: "DD/MM/YYYY HH:mm",
  },
  {
    title: "Created On",
    key: "createdOn",
    width: 168,
    sort: true,
    format: "DD/MM/YYYY HH:mm",
  },
  { title: "Created By", key: "createdByUserUserName", width: 168, sort: true },
];

const receiptsQueryMappings: QueryFieldMapping[] = [
  { formField: "company", queryField: "companyUid", operation: QueryOperation.Equal, transform: comboboxValue },
  { formField: "department", queryField: "departmentUid", operation: QueryOperation.Equal, transform: comboboxValue },
  { formField: "user", queryField: "userUid", operation: QueryOperation.Equal, transform: comboboxValue },
  { formField: "notificationTitle", operation: QueryOperation.Contains },
  { formField: "notificationMessage", operation: QueryOperation.Contains },
  { formField: "deliveryStatus", operation: QueryOperation.Equal, transform: comboboxValue },
  { formField: "isRead", operation: QueryOperation.Equal },
  { formField: "readAt", operation: QueryOperation.Between, transform: dateRangeValue },
  { formField: "createdOn", operation: QueryOperation.Between, transform: dateRangeValue },
];

export const mapReceiptsViewQuery = (values: ReceiptsViewForm): SearchQuery =>
  buildSearchQuery(values, receiptsQueryMappings);

// Factory so the User / Department pickers narrow by the chosen Company / Department (soft scope;
// `dependsOn` clears stale children). Admins still search platform-wide until a parent is set.
export const receiptsSearchSchema = (
  form: FormInstance<any>,
): DynamicSchema => ({
  groups: [
    {
      controls: [
        {
          type: "combobox",
          name: "company",
          label: "Company",
          request: createSearchRequest(getCompanies, "name"),
        },
        {
          type: "combobox",
          name: "department",
          label: "Department",
          dependsOn: ["company"],
          request: createScopedFilterRequest(getCompanyDepartments, "name", () => [
            ["companyUid", form.getFieldValue("company")?.value],
          ]),
        },
        {
          type: "combobox",
          name: "user",
          label: "User",
          dependsOn: ["company", "department"],
          request: createScopedFilterRequest(getPlatformUsers, "userName", () => [
            ["companyUid", form.getFieldValue("company")?.value],
            ["departmentUid", form.getFieldValue("department")?.value],
          ]),
        },
        { type: "textbox", name: "notificationTitle", label: "Title" },
        { type: "textbox", name: "notificationMessage", label: "Message" },
        {
          type: "combobox",
          name: "deliveryStatus",
          label: "Delivery",
          request: () => Promise.resolve(deliveryStatusOptions),
        },
        { type: "togglebox", name: "isRead", label: "Read" },
        { type: "rangetimepicker", name: "readAt", label: "Read At" },
        { type: "rangetimepicker", name: "createdOn", label: "Created On" },
      ],
    },
  ],
});
